import { DataTypes } from "@lexriver/dome"
import { JsonMap } from '@lexriver/json-map'
import { ObservableLocalStorageArray, ObservableLocalStorageVariable, ObservableMap } from "@lexriver/observable"
import { StringMethods } from '@lexriver/string-methods'
import { TypeEvent } from '@lexriver/type-event'
import { Ecom } from "../Ecom/Ecom"
// import { FacebookPixel } from "../FacebookPixel"
import { GlobalState } from "../GlobalState"
import { JsonBasketProductInfo, checkIfValidJsonBasketProductInfo } from "../json-data-types/JsonBasketProductInfo"
import { JsonBasketRow } from "../json-data-types/JsonBasketRow"
import { JsonBasketStats } from "../json-data-types/JsonBasketStats"
import { JsonCoupon } from "../json-data-types/JsonCoupon"
import { JsonOrderItem } from "../json-data-types/JsonOrderItem"
import { JsonProduct } from "../json-data-types/JsonProduct"
import { RequestMaker } from "../server-queries/RequestMaker"
import { ServerCoupons } from "../server-queries/ServerCoupons"
import { ServerProducts } from "../server-queries/ServerProducts"
import { ChosenAttributes, IBasket } from "./IBasket"

export class BasketInLocalStorage implements IBasket {
    protected quantityByProductInfoString = new ObservableMap<string, number>()

    protected orderItems: JsonOrderItem[] = []
    public onChangeEvent: TypeEvent<() => void> = new TypeEvent()
    public onBasketEmptyOrNotEvent: TypeEvent<(isEmpty: boolean) => void> = new TypeEvent()

    public onCouponChangeEvent: TypeEvent<() => void> = new TypeEvent()
    protected prevIsEmpty: boolean = true

    protected couponCodesInLocalStorage = new ObservableLocalStorageArray<string>({
        localStorageKey: 'AnonymousBasketV3Coupons'
    })

    protected statsPromiseByRequestKey = new Map<string, Promise<JsonBasketStats>>()


    constructor() {
        // console.log('ctor: BasketInLocalStorage')
        //#region create basketInLocalStorage as ObservableLocalStorageValue => update this.quantityByProductInfoString
        const basketInLocalStorage = new ObservableLocalStorageVariable<[string, number][]>({
            localStorageKey: 'AnonymousBasketV2',
            // validatorFunction: (newValue) => { // validator
            //     if(newValue === undefined) return true
            //     if(Data.isArray(newValue)) return true
            //     return false
            // }
        })
        basketInLocalStorage.eventOnChange.subscribe(async (newValue) => {
            const currentValueAsJsonString = this.toJsonString()
            const newValueInLocalStorageAsString = JSON.stringify(newValue)
            if (currentValueAsJsonString !== newValueInLocalStorageAsString) {
                // we have a new basket from other window
                // this.quantityByProductInfoString.initFromArray(newValue as [])
                await this.initFromArrayAsync(newValue as [])
                await this.onChangeEvent.triggerAsync()
            }

        })
        //#endregion

        //#region Create promo-codes storage
        this.couponCodesInLocalStorage.eventOnChange.subscribe(async (newValue) => {
            await this.onCouponChangeEvent.triggerAsync()
        })
        //#endregion

        const couponCodes = this.couponCodesInLocalStorage.getAsCopy()
        if (couponCodes && couponCodes.length > 0) {
            const dedupedCouponCodes = this.getCouponCodesWithoutCaseDuplicates(couponCodes)
            if (dedupedCouponCodes.length !== couponCodes.length) {
                this.couponCodesInLocalStorage.set(dedupedCouponCodes)
            }
        }

        //#region init this.quantityByProductInfoString from localStorage
        const currentValueInLocalStorage = basketInLocalStorage.get()
        if (currentValueInLocalStorage) {
            void this.initFromArrayAsync(currentValueInLocalStorage)
            // this.quantityByProductInfoString.initFromArray(currentValueInLocalStorage)
            this.prevIsEmpty = this.quantityByProductInfoString.isEmpty()
            //console.log('prevIsEmpty=', this.prevIsEmpty)
        }
        //#endregion


        //#region trigger this.onBasketEmptyOrNotEvent if needed
        this.onChangeEvent.subscribe(async () => {
            //console.log('BasketInLocalStorage.ts onChangeEvent')
            const isEmptyNow = this.quantityByProductInfoString.isEmpty()
            //console.log('prevIsEmpty=', this.prevIsEmpty, 'isEmptyNow=', isEmptyNow)
            if (this.prevIsEmpty !== isEmptyNow) {
                //console.log('BasketInLocalStorage.ts triggering onBasketEmptyOrNotEvent')
                await this.onBasketEmptyOrNotEvent.triggerAsync(isEmptyNow)
                this.prevIsEmpty = isEmptyNow
            }
        })
        //#endregion

        //#region when modify this.quantityByProductInfoString => update localStorage && trigger this.onChangeEvent
        this.quantityByProductInfoString.eventOnChange.subscribe(async (key?: string, value?: number) => {
            basketInLocalStorage.set(this.quantityByProductInfoString.toArray())
            await this.onChangeEvent.triggerAsync()
        })
        this.quantityByProductInfoString.eventOnDeleteKey.subscribe(async (key: string) => {
            basketInLocalStorage.set(this.quantityByProductInfoString.toArray())
            await this.onChangeEvent.triggerAsync()
        })
        this.quantityByProductInfoString.eventOnClear.subscribe(async () => {
            basketInLocalStorage.set(this.quantityByProductInfoString.toArray())
            await this.onChangeEvent.triggerAsync()
        })
        //#endregion

        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.onChangeEvent.triggerAsync()

    }


    protected getCouponCodesWithoutCaseDuplicates(codes: string[]): string[] {
        const result: string[] = []
        const lowered = new Set<string>()
        for (const code of codes) {
            if (!code) {
                continue
            }
            const normalized = code.trim()
            const lower = normalized.toLowerCase()
            if (lowered.has(lower)) {
                continue
            }
            lowered.add(lower)
            result.push(normalized)
        }
        return result
    }

    protected getNormalizedCouponCodes(): string[] {
        const codes = this.couponCodesInLocalStorage.getAsCopy()
        return this.getCouponCodesWithoutCaseDuplicates(codes)
    }

    protected removeCouponCodeIgnoreCase(code: string): void {
        if (!code) {
            return
        }
        const lower = code.trim().toLowerCase()
        const allCodes = this.couponCodesInLocalStorage.getAsCopy()
        let changed = false
        const result: string[] = []
        for (const existing of allCodes) {
            if (!changed && existing && existing.toLowerCase() === lower) {
                changed = true
                continue
            }
            result.push(existing)
        }
        if (changed) {
            this.couponCodesInLocalStorage.set(result)
        }
    }

    protected upsertCouponCode(code: string): void {
        if (!code) {
            return
        }
        const normalized = code.trim()
        const lower = normalized.toLowerCase()
        const allCodes = this.couponCodesInLocalStorage.getAsCopy()
        const result: string[] = []
        let found = false
        for (const existing of allCodes) {
            if (!found && existing && existing.toLowerCase() === lower) {
                found = true
                result.push(normalized)
            } else {
                result.push(existing)
            }
        }
        if (!found) {
            result.push(normalized)
        }

        const normalizedResult = this.getCouponCodesWithoutCaseDuplicates(result)
        if (JSON.stringify(allCodes) !== JSON.stringify(normalizedResult)) {
            this.couponCodesInLocalStorage.set(normalizedResult)
        }
    }



    protected getStringKeyFromProductInfo(productInfo: JsonBasketProductInfo) {
        return JSON.stringify(productInfo)
    }

    protected getProductInfoFromString(productInfoAsString: string) {
        const result = JSON.parse(productInfoAsString) as JsonBasketProductInfo
        if (checkIfValidJsonBasketProductInfo(result)) {
            return result
        }
        throw new Error('invalid productInfo in string: ' + productInfoAsString)
    }

    async addAsync(product: JsonProduct, quantity: number, chosenAttributes?: ChosenAttributes) {
        const productInfo: JsonBasketProductInfo = {
            productId: product.id,
            chosenAttributes
        }
        const key = this.getStringKeyFromProductInfo(productInfo)
        const cQuantity = this.quantityByProductInfoString.get(key)
        if (cQuantity) {
            // we alreayd have this product, so increase quantity
            this.quantityByProductInfoString.set(key, cQuantity + quantity)
        }
        else {
            this.quantityByProductInfoString.set(key, quantity)
        }
        await Ecom.addProductToBasketAsync(product, quantity, chosenAttributes)
        // void FacebookPixel.addProductToBasketAsync(product, quantity, chosenAttributes)

        return true
    }

    async changeQuantityAsync(product: JsonProduct, quantity: number, chosenAttributes?: ChosenAttributes) {
        const productInfo: JsonBasketProductInfo = {
            productId: product.id,
            chosenAttributes
        }
        const currentQuantity = this.getQuantity(product, chosenAttributes)
        if (quantity > currentQuantity) {
            await Ecom.addProductToBasketAsync(product, quantity - currentQuantity, chosenAttributes)
            // void FacebookPixel.addProductToBasketAsync(product, quantity, chosenAttributes)

        } else if (quantity < currentQuantity) {
            await Ecom.removeProductFromBasketAsync(product, currentQuantity - quantity, chosenAttributes)
        }
        const key = this.getStringKeyFromProductInfo(productInfo)
        this.quantityByProductInfoString.set(key, quantity)
        return true
    }

    /**
     * get quantity by productInfo or return 0
     * @param productInfo
     */
    getQuantity(product: JsonProduct, chosenAttributes?: ChosenAttributes): number {
        const productInfo: JsonBasketProductInfo = {
            productId: product.id,
            chosenAttributes
        }
        const key = this.getStringKeyFromProductInfo(productInfo)
        return this.quantityByProductInfoString.get(key) || 0
    }

    getQuantityByProductId(productId: number): number {
        //TODO: optimize this?
        let result = 0
        for (let [k, v] of this.quantityByProductInfoString.toArray()) {
            let productInfo: JsonBasketProductInfo = JSON.parse(k) as JsonBasketProductInfo
            if (productInfo.productId == productId) {
                result += v
            }
        }
        return result
    }

    async removeAsync(product: JsonProduct, chosenAttributes?: ChosenAttributes) {
        const productInfo: JsonBasketProductInfo = {
            productId: product.id,
            chosenAttributes
        }
        const key = this.getStringKeyFromProductInfo(productInfo)
        const cQuantity = this.getQuantity(product, chosenAttributes)
        await Ecom.removeProductFromBasketAsync(product, cQuantity, chosenAttributes)
        this.quantityByProductInfoString.delete(key)
        return true
    }

    // protected productsToRemove:JsonMap<JsonBasketProductInfo, boolean> = new JsonMap<JsonBasketProductInfo, boolean>()
    protected productsToRemove: JsonMap<JsonBasketProductInfo, JsonProduct> = new JsonMap<JsonBasketProductInfo, JsonProduct>()

    markProductToRemove(product: JsonProduct, chosenAttributes?: ChosenAttributes) {
        const productInfo: JsonBasketProductInfo = {
            productId: product.id,
            chosenAttributes
        }
        this.productsToRemove.set(productInfo, product)
    }

    unmarkProductToRemove(product: JsonProduct, chosenAttributes?: ChosenAttributes) {
        const productInfo: JsonBasketProductInfo = {
            productId: product.id,
            chosenAttributes
        }
        this.productsToRemove.delete(productInfo)
    }

    checkIfProductIsMarkedToRemove(product: JsonProduct, chosenAttributes?: ChosenAttributes): boolean {
        const productInfo: JsonBasketProductInfo = {
            productId: product.id,
            chosenAttributes
        }
        // console.log('checkIfProductIsMarkedToRemove', productInfo, this.productsToRemove.has(productInfo))
        return this.productsToRemove.has(productInfo)

    }

    unmarkAllProducts() {
        console.log('unmarkAllProducts')
        this.productsToRemove.clear()
    }

    async removeMarkedProductsAsync(): Promise<void> {
        for (let [basketProductInfo, product] of this.productsToRemove) {
            await this.removeAsync(product, basketProductInfo.chosenAttributes)
        }
        this.productsToRemove.clear()
    }

    async notifyEcomOfRemovingAllProductsFromBasketAsync() {
        await this.iterateProductsAsync(async (productInfo, quantity, product) => {
            await Ecom.removeProductFromBasketAsync(product, quantity, productInfo.chosenAttributes)
        })
    }

    async clearAsync() {
        await this.notifyEcomOfRemovingAllProductsFromBasketAsync()
        this.quantityByProductInfoString.clear()
        this.couponCodesInLocalStorage.set([])
        return true
    }

    async iterateProductsAsync(action: (productInfo: JsonBasketProductInfo, quantity: number, product: JsonProduct) => Promise<void> | void) {
        let productById = await ServerProducts.getProductsByIdsAsync(
            this.getProductIds(),
            GlobalState.productShowZeroQuantityProductsO.get() ?? false
        )
        for (let [k, v] of this.quantityByProductInfoString.toArray()) {
            let productInfo: JsonBasketProductInfo = JSON.parse(k) as JsonBasketProductInfo
            let quantity = v
            let product = productById[productInfo.productId]

            if (!product) {
                console.error('unable to get product by id', productInfo.productId)
            } else {
                await action(productInfo, quantity, product)
            }

        }
    }

    protected getStatsRequestKey(p: { getJsonOrderItems: boolean }): string {
        return JSON.stringify({
            quantityByJsonBasketProductInfo: this.quantityByProductInfoString.toArray(),
            couponCodes: this.getNormalizedCouponCodes(),
            getJsonOrderItems: p.getJsonOrderItems
        })
    }

    protected getStatsStateKey(): string {
        return JSON.stringify({
            quantityByJsonBasketProductInfo: this.quantityByProductInfoString.toArray(),
            couponCodes: this.getNormalizedCouponCodes()
        })
    }

    async getStatsAsync(p: { getJsonOrderItems: boolean }): Promise<JsonBasketStats> {
        const requestKey = this.getStatsRequestKey(p)
        const existingPromise = this.statsPromiseByRequestKey.get(requestKey)
        if (existingPromise) {
            return existingPromise
        }

        const promise = this.loadStatsAsync(p)
        this.statsPromiseByRequestKey.set(requestKey, promise)
        try {
            return await promise
        } finally {
            this.statsPromiseByRequestKey.delete(requestKey)
        }
    }

    protected async loadStatsAsync(p: { getJsonOrderItems: boolean }): Promise<JsonBasketStats> {
        // params:
        // quantityByJsonBasketProductInfo:any[]
        const stateKeyBeforeRequest = this.getStatsStateKey()
        const couponCodesInBasket = this.getNormalizedCouponCodes()
        console.log('BasketInLocalStorage.ts getStatsAsync coupons currently in basket', couponCodesInBasket)
        const result = await RequestMaker.sendWebsocketJsonRequestAsync({
            query: 'basket-stats-anonymous',
            params: {
                quantityByJsonBasketProductInfo: this.quantityByProductInfoString.toArray()
                    .map(([productInfoString, quantity]) => [JSON.parse(productInfoString) as JsonBasketProductInfo, quantity]),
                couponCodes: couponCodesInBasket,
                getJsonOrderItems: p.getJsonOrderItems
            }
        }, this) as JsonBasketStats

        console.log('basket-stats-anonymous', 'result=', result, 'coupons currently in basket=', this.getNormalizedCouponCodes())

        if (stateKeyBeforeRequest !== this.getStatsStateKey()) {
            return await this.getStatsAsync(p)
        }

        if (result.jsonOrderItems) {
            this.orderItems = result.jsonOrderItems
        }

        if(result.productIdsToRemove){
            for(const productId of result.productIdsToRemove){
                const key = this.getStringKeyFromProductInfo({productId})
                this.quantityByProductInfoString.delete(key)
            }
        }

        if(result.couponCodesToRemove){
            for(const couponCode of result.couponCodesToRemove){
                this.removeCouponCodeIgnoreCase(couponCode)
            }
        }


        // update coupons if we have some auto-apply coupons on server.
        // Do not remove zero-discount coupons here: manually applied coupons should stay visible
        // and removable even when they currently do not affect the basket.
        if (result.discountAmountByCouponCode) {
            for (const [code, discountAmount] of Object.entries(result.discountAmountByCouponCode)) {
                if(discountAmount > 0){
                    this.upsertCouponCode(code)
                }
            }
        }

        return result

    }


    async getCouponsAsync(): Promise<JsonCoupon[]> {
        let result: JsonCoupon[] = []
        let codesToDelete: string[] = []
        for (let code of this.couponCodesInLocalStorage.getAsCopy()) {
            try {
                let coupon = await ServerCoupons.getAsync(code)
                if (coupon) {
                    result.push(coupon)
                } else {
                    this.couponCodesInLocalStorage.removeFirst(code)
                }
            } catch (x) {
                console.warn(x, code)
                codesToDelete.push(code)
            }
        }
        return result

    }

    toJsonString(): string {
        return JSON.stringify(this.quantityByProductInfoString.toArray())
    }

    initFromJsonString(jsonString: string) {
        const asArray = JSON.parse(jsonString)
        // this.quantityByProductInfoString.initFromArray(asArray)
        void this.initFromArrayAsync(asArray)
    }

    async initFromArrayAsync(array: any[]) {
        this.quantityByProductInfoString.initFromArray(array)
        await this.removeObsoleteProductsAsync()
    }

    /**
     * Remove products that are not available for this user
     */
    async removeObsoleteProductsAsync() {
        const productIds: number[] = []
        for (let key of this.quantityByProductInfoString.keys()) {
            try {
                productIds.push(this.getProductInfoFromString(key).productId)

            } catch (error) {
                console.error(error)
            }
        }

        const avaliableProductIds = await ServerProducts.validateProductIds(productIds, GlobalState.productShowZeroQuantityProductsO.get() ?? false)
        console.log('productIds=', productIds, 'availableProductIds=', avaliableProductIds)
        for (let key of this.quantityByProductInfoString.keys()) {
            try {
                const productInfo = this.getProductInfoFromString(key)
                if (avaliableProductIds.includes(productInfo.productId)) {
                    // ok
                    console.log(`product ${productInfo.productId} available`)
                } else {
                    console.warn(`product ${productInfo.productId} obsolete!`)
                    this.quantityByProductInfoString.delete(key)
                }
            } catch (error) {
                console.error(error)
            }
        }
    }

    getHash(): number {
        return StringMethods.getSimpleHash(this.toJsonString())
    }

    getProductIds(): number[] {
        let result: number[] = []
        for (let [k, v] of this.quantityByProductInfoString.toArray()) {
            let productInfo: JsonBasketProductInfo = JSON.parse(k) as JsonBasketProductInfo
            let quantity = v
            result.push(productInfo.productId)
        }
        return result
    }

    async getListOfRowsAsync(): Promise<Array<JsonBasketRow>> {
        // 
        // console.log('getListOfRowsAsync', caller)
        // debugger
        let basketStats = await this.getStatsAsync({ getJsonOrderItems: true })
        let coupons = await this.getCouponsAsync()
        let result: Array<JsonBasketRow> = []

        // TODO: we need update each row sum with discount!




        await this.iterateProductsAsync(async (productInfo, quantity, product) => {
            const orderItem = this.getOrderItem({
                orderItems: basketStats.jsonOrderItems,
                productId: product.id,
                productAttributes: productInfo.chosenAttributes
            })
            let row: JsonBasketRow = {
                product,
                productChosenAttributes: productInfo.chosenAttributes,
                quantity,
                // discountByCouponCodeInRub:basketStats.jsonOrderItems?.find(x => x.productId === product.id && x.attributes === productInfo.chosenAttributes)?.priceDiscountByCouponCode ?? {}
                discountByCouponCodeInRub: orderItem?.priceDiscountByCouponCode || {},
                lineDiscountKopecksByCouponCode: orderItem?.lineDiscountKopecksByCouponCode,
                unitFinalPriceKopecks: orderItem?.unitFinalPriceKopecks
            }

            result.push(row)

        })

        // sort by product sku
        result.sort((a, b) => a.product.sku.localeCompare(b.product.sku))

        return result
    }

    getOrderItem(p: { orderItems?: JsonOrderItem[], productId: number, productAttributes?: ChosenAttributes }) {
        if (!p.orderItems) return undefined
        for (const item of p.orderItems) {
            if (item.productId === p.productId && DataTypes.isEqual(p.productAttributes, item.attributes)) {
                return item
            }
        }
        return undefined
    }

    getDiscountByCouponCode(p: { orderItems?: JsonOrderItem[], productId: number, productAttributes?: ChosenAttributes }) {
        return this.getOrderItem(p)?.priceDiscountByCouponCode || {}
    }

    getListOfCouponCodes(): string[] {
        return this.getNormalizedCouponCodes()
    }

    isEmpty() {
        //console.log('@@ this.quantityByProductInfostring=', this.quantityByProductInfoString)
        return this.quantityByProductInfoString.isEmpty()
    }

    isInitialized() {
        return true
    }

    async applyCouponAsync(code: string): Promise<void> {
        try {
            const couponCodesInBasket = this.getNormalizedCouponCodes()
            const quantityByJsonBasketProductInfo = this.quantityByProductInfoString.toArray()
                .map(([productInfoString, quantity]) => [JSON.parse(productInfoString) as JsonBasketProductInfo, quantity])
            console.log('BasketInLocalStorage.ts applyCouponAsync before request', {code, couponCodesInBasket, quantityByJsonBasketProductInfo})
            const coupon = await ServerCoupons.applyCouponToAnonymousBasketAsync({
                couponCode: code,
                couponCodesInBasket,
                quantityByJsonBasketProductInfo
            })
            console.log('BasketInLocalStorage.ts applyCouponAsync response', {code, coupon})
            if (coupon) {
                this.upsertCouponCode(coupon.code)
                console.log('BasketInLocalStorage.ts applyCouponAsync after upsert', {couponCodesInBasket: this.getNormalizedCouponCodes()})
                return
            }
        } catch (error) {
            console.error(error)
            throw error
        }
    }

    async removeCouponAsync(code: string): Promise<void> {
        this.removeCouponCodeIgnoreCase(code)

    }

    getJsonOrderItems(): JsonOrderItem[] {
        return this.orderItems
    }

}
